<template>
  <div
    class="h-10 bg-black-card flex items-center justify-center rounded-lg overflow-hidden"
  >
    <div
      v-for="(tab, index) in themeTypeTabs"
      :key="index"
      class="flex-1 h-full"
      :class="{
        ' bg-black-active rounded-lg': tab.type == activeThemeType,
        ' cursor-pointer': tab.type != activeThemeType
      }"
      @click="() => onClickTab(tab.type)"
    >
      <drop-down
        trigger="click"
        :menus="index == 1 && dropdownEnabled ? dropdownMenus : []"
        placement="bottom"
        :width="160"
      >
        <div class="flex items-center justify-center h-full">
          <span class="font-semibold text-xs">
            {{
              index > 0
                ? AODThemeCount > 0
                  ? dropdownMenus.find((item) => item.selected)?.title ??
                    tab.title
                  : tab.title
                : tab.title
            }}
          </span>

          <div v-if="index == 1">
            <div
              class="cursor-pointer pr-2 h-full flex items-center justify-center"
            >
              <icon
                class="ml-1"
                :icon="'arrow-down.svg'"
                :width="12"
                fill="none"
              />
            </div>
          </div>
        </div>
      </drop-down>
    </div>
  </div>
  <!-- {{ menus }} -->
</template>

<script setup lang="ts">
import { useThemeManage } from '@/hooks';
import { useLayerStore } from '@/store/use-layer-store';
import { useEditorStore } from '@/store/use-editor-store';
import { computed, ref, h, watch, nextTick } from 'vue';
import { Button, notification } from 'ant-design-vue';
import { canUse } from '@/utils/can-use.utils';

const editorStore = useEditorStore();

const { exchangeAODMode, addAODTheme } = useThemeManage();

const layerStore = useLayerStore();

const normalThemes = computed(() =>
  layerStore.themes.filter((theme) => theme.type == 'normal')
);

const activeThemeType = ref<'normal' | 'AOD'>('normal');
const themeTypeTabs = computed(() => {
  const items: {
    type: StoreThemeItem['type'];
    title: string;
    hidden: boolean;
  }[] = [
    {
      type: 'normal',
      title: '亮屏',
      hidden: false
    },
    {
      type: 'AOD',
      title: '未设置息屏',
      hidden: canUse('single-AOD-themes').valid ? false : true
    }
  ];

  return items.filter((item) => !item.hidden);
});

// 首次进入息屏需要显示提示
const isFirstEnterAOD = ref(false);
const showAODNotification = () => {
  if (
    isFirstEnterAOD.value ||
    !AODThemeCount.value ||
    !editorStore.theme?.children.length
  )
    return;
  isFirstEnterAOD.value = true;
  const themeTreeDiv = document.getElementById('themeTree');
  const { bottom, right } = themeTreeDiv?.getBoundingClientRect() ?? {};
  const key = `open${Date.now()}`;
  isFirstEnterAOD.value = true;
  notification.info({
    message: '删除息屏下不需要的图层',
    placement: 'topLeft',
    top: (bottom ?? 100) + 10 + 'px',
    style: {
      left: (right ?? 100) - 20 + 'px'
    },
    btn: () =>
      h(
        Button,
        {
          type: 'default',
          size: 'small',
          onClick: () => notification.close(key)
        },
        { default: () => '知道了' }
      ),
    key
  });
};

const onClickTab = (type: StoreThemeItem['type']) => {
  activeThemeType.value = type;

  // 如果当前没有息屏，则自动创建一个单息屏
  if (type == 'AOD' && !AODThemeCount.value) {
    addAODTheme(normalThemes.value?.[0], true);
  }

  reSelectThemeIdIfNeed(type);
  if (type == 'AOD') {
    // 第一次进入息屏需要进行提示
    showAODNotification();
  }
  if (type == 'AOD') {
    dropdownEnabled.value = true;
  } else {
    dropdownEnabled.value = false;
  }
};

const AODThemeCount = computed(
  () => layerStore.themes.filter((item) => item.type == 'AOD')?.length ?? 0
);

const dropdownEnabled = ref(false);
const dropdownMenus = computed(() => {
  const res: DropDownMenuItem[] = [
    {
      title: '单一息屏',
      selected: layerStore.isSingleAOD,
      onClick: () => selectMode('single')
    },
    {
      title: '多配色息屏',
      selected: layerStore.isMultiColorAOD,
      onClick: () => selectMode('multiColor'),
      hidden: canUse('multi-AOD-themes').valid ? false : true
    }
  ].filter((item) => (item.hidden ? false : true));
  return res;
});

const selectMode = (mode: 'single' | 'multiColor') => {
  if (activeThemeType.value !== 'AOD') onClickTab('AOD');
  if (mode == 'single' && layerStore.isSingleAOD) {
    return;
  }
  if (mode == 'multiColor' && layerStore.isMultiColorAOD) {
    return;
  }
  exchangeAODMode(mode);
  reSelectThemeIdIfNeed('AOD');
};

const reSelectThemeIdIfNeed = (type: 'normal' | 'AOD') => {
  // 判断当前选择是否为正确的模式
  const theme = layerStore.findTheme(editorStore.themeId);
  // 当前没有选中的
  if (!theme) {
    // 选中type类型的第一个
    editorStore.selectTheme(undefined, type);
  } else {
    // 当前有选中的，但是type不对
    if (theme.type != type) {
      const theme2 = layerStore.themes.find(
        (item) =>
          item.name == theme.name &&
          item.type == (theme.type == 'AOD' ? 'normal' : 'AOD')
      );
      editorStore.selectTheme(theme2?.id, type);
    }
  }
};

watch(
  () => editorStore.themeId,
  () => {
    const type = editorStore.theme?.type ?? 'normal';
    nextTick(() => {
      if (type !== activeThemeType.value) {
        activeThemeType.value = type;
      }
    });
  }
);
</script>
